/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-28
 */

import { useState, useEffect } from 'react';

/**
 * Cache status types
 */
export type CacheStatus = 'live' | 'cached' | 'unknown';

/**
 * Cache status data interface
 */
export interface CacheStatusData {
  status: CacheStatus;
  lastUpdated: Date | null;
  isOnline: boolean;
}

/**
 * Service worker message interface
 */
interface ServiceWorkerMessage {
  type: 'CACHE_STATUS';
  payload: {
    fromCache: boolean;
    url: string;
    timestamp: number;
  };
}

/**
 * Custom hook to track cache status and network connectivity
 *
 * @returns {CacheStatusData} Current cache status information
 *
 * @example
 * const { status, lastUpdated, isOnline } = useCacheStatus();
 *
 * if (status === 'live') {
 *   // Show live indicator
 * } else if (status === 'cached') {
 *   // Show cached indicator
 * }
 */
export const useCacheStatus = (): CacheStatusData => {
  const [status, setStatus] = useState<CacheStatus>('unknown');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  useEffect(() => {
    // Track online/offline status
    const handleOnline = () => {
      setIsOnline(true);
      // When coming back online, assume next requests will be live
      setStatus('live');
    };

    const handleOffline = () => {
      setIsOnline(false);
      // When offline, content will be served from cache
      setStatus('cached');
    };

    // Listen for network status changes
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for service worker messages about cache status
    const handleServiceWorkerMessage = (
      event: MessageEvent<ServiceWorkerMessage>
    ) => {
      if (event.data && event.data.type === 'CACHE_STATUS') {
        const { fromCache, timestamp } = event.data.payload;

        setStatus(fromCache ? 'cached' : 'live');
        setLastUpdated(new Date(timestamp));
      }
    };

    // Register service worker message listener
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener(
        'message',
        handleServiceWorkerMessage
      );

      // Listen for when service worker controller becomes available
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'GET_CACHE_STATUS',
          });
        }
      });
    }

    // Initial status determination
    if (!isOnline) {
      setStatus('cached');
    } else if (
      'serviceWorker' in navigator &&
      navigator.serviceWorker.controller
    ) {
      // Check if we have a service worker and recent cache activity
      // Request current status from service worker
      navigator.serviceWorker.controller.postMessage({
        type: 'GET_CACHE_STATUS',
      });
    } else {
      // No service worker, assume live
      setStatus('live');
    }

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener(
          'message',
          handleServiceWorkerMessage
        );
      }
    };
  }, [isOnline]);

  return {
    status,
    lastUpdated,
    isOnline,
  };
};

export default useCacheStatus;
